<template>
  <HomeSectionNavigation v-if="phone && route.path !== eventHomePath && !developmentProgram && eventMode === 'staff' && !loading" :model-value="activeSection" :groups="navigation.groups" :label="t('tryoutHome.navigation.label')" accordion fallback @update:model-value="selectSection" @activate-locked="selectSection('journals')" />
  <ResponsivePageNavigation v-else-if="phone && (route.path !== eventHomePath || eventMode === 'athlete')" ref="phoneSidebar" :label="t('navigation.eventSidebar')" fallback>
    <nav class="event-navigation-sidebar" :aria-label="t('navigation.eventSidebar')">
      <button v-for="action in actions" :key="action.key" type="button" :disabled="action.disabled || loading" @click="runAction(action);phoneSidebar?.close()"><v-icon :icon="action.icon" size="20" /><span><strong>{{ action.title }}</strong><small v-if="action.subtitle">{{ action.subtitle }}</small></span></button>
      <p v-if="!loading && !actions.length">{{ t('shell.eventActions.empty') }}</p>
    </nav>
  </ResponsivePageNavigation>
</template>

<script setup lang="ts">
import {eventViewAccess} from '@/stores/eventView'
  import ResponsivePageNavigation from './ResponsivePageNavigation.vue'
  import {useCompactPageNavigation} from '@/composables/useCompactLayout'
  import {ref} from 'vue'
  import HomeSectionNavigation from './HomeSectionNavigation.vue'
  import {eventHomeNavigation} from '@/features/tryout-home/eventHomeNavigation'
  import {pluralizeModelNoun} from '@/utils/modelNouns'
  import { useAuthStore } from '@/stores/auth'
  import { useEventViewStore, resolveEventView } from '@/stores/eventView'
  import { useBetaStore } from '@/stores/beta'
  import { computed, toRef } from 'vue'
  import {useEventTerminology} from '@/composables/useEventTerminology'
  import { useRoute, useRouter } from 'vue-router'
  import { useTryoutHome } from '@/features/tryout-home/composables'
  import { useTryoutContext, useTryoutSessionInvalidation } from '@/composables/useTryoutContext'
  import { useSeasonEvents, isDevelopmentProgram } from '@/composables/useSeasonEvents'
  import {useAthleteFeedback} from '@/features/athlete-feedback/useAthleteFeedback'
  import { programActions } from '@/features/development-program/actions'

  interface EventAction {
    key: string
    title: string
    icon: string
    to?: string
    disabled?: boolean
    subtitle?: string
  }

  const props = defineProps<{
    clubId: string
    seasonId: string
    tryoutId: string
  }>()

  const phone=useCompactPageNavigation(),phoneSidebar=ref<InstanceType<typeof ResponsivePageNavigation>>()
  const { t } = useEventTerminology(()=>homeData.value?.event)
  const router = useRouter(), route = useRoute()
  const clubId = toRef(props, 'clubId'), seasonId = toRef(props, 'seasonId'), tryoutId = toRef(props, 'tryoutId')
  const homeQuery = useTryoutHome(seasonId, tryoutId, clubId)
  const contextQuery = useTryoutContext(clubId, seasonId, tryoutId, computed(() => eventViewAccess(homeQuery.data.value?.event.roles).staff))
  useTryoutSessionInvalidation(clubId, seasonId, tryoutId)
  const { query: seasonQuery, events } = useSeasonEvents(clubId, seasonId)
  const loading = computed(() => homeQuery.isPending.value || contextQuery.isPending.value || seasonQuery.isPending.value)
  const homeData = homeQuery.data, contextData = contextQuery.data
  const developmentProgram = computed(() => isDevelopmentProgram(events.value.find(event => event.id === tryoutId.value)?.eventType))

  const roles = computed(() => homeData.value?.event.roles || {})
  const auth = useAuthStore(), eventViews = useEventViewStore()
  const eventMode = computed(() => resolveEventView(eventViews.preference({viewerId: String(auth.user?.details?.id || auth.user?.id || ''), clubId: props.clubId, seasonId: props.seasonId, eventId: props.tryoutId}), roles.value))
  const betaStore = useBetaStore()
  const beta = computed(() => betaStore.enabled)
  const participantNoun = computed(() =>
    homeData.value?.event.participantNoun ||
    t('tryoutHome.defaults.participantNoun')
  )
  const participantLabel = computed(() => capitalize(participantNoun.value))
  const sessionNoun = computed(() => homeData.value?.event.sessionNoun || t('tryoutHome.defaults.sessionNoun'))

  const canManageEvent = computed(() => contextData.value?.permissions?.canManageEvent === true)
  const allowSettings = computed(() => canManageEvent.value)
  const allowFeedback = computed(() => hasAnyRole(['admin', 'owner', 'sysadmin', 'headcoach', 'programcoach', 'assistantcoach', 'selector', 'support']))
  const allowParticipantList = computed(() => hasAnyRole(['admin', 'owner', 'headcoach', 'assistantcoach', 'selector']))
  const allowSelectTeams = computed(() => hasAnyRole(['admin', 'owner', 'sysadmin', 'headcoach', 'assistantcoach', 'programcoach']))
  const allowScanScoresheets = computed(() => hasAnyRole(['owner', 'admin', 'sysadmin', 'selector', 'headcoach', 'assistantcoach', 'programcoach']))

  const eventHomePath = computed(() => `/clubhouse/${props.clubId}/season/${props.seasonId}/tryout/${props.tryoutId}`)

  const actions = computed<EventAction[]>(() => {
    if (seasonQuery.isPending.value || seasonQuery.isError.value || !eventMode.value) return []
    if (!developmentProgram.value && eventMode.value === 'athlete') return [
      ...(homeData.value?.event.allowJournal ? [{key: 'myJournal', title: t('tryoutHome.actions.myJournal'), icon: 'mdi-notebook-outline', to: `${eventHomePath.value}/journal`}, {key:'myJournal2',title:t('journalAlternative.mine'),icon:'mdi-notebook-outline',to:`${eventHomePath.value}/journal-2`}] : []),
      { key:'eventHome', title:t('tryoutHome.actions.eventHome'), icon:'mdi-home-outline', to:eventHomePath.value },
      { key:'feedback', title:t('tryoutHome.actions.feedback'), icon:'mdi-comment-quote-outline', to:`${eventHomePath.value}?section=feedback` },
    ]
    if (developmentProgram.value) return programActions(eventMode.value === 'staff' ? {...roles.value, athlete: false} : roles.value, eventMode.value === 'athlete' ? 'athlete' : 'staff', beta.value, homeData.value?.features?.scouting.available === true).map(action => ({
      ...action, title: t(`developmentProgram.actions.${action.key}`, { participant: participantLabel.value }),
      to: action.destination ? `${eventHomePath.value}${action.destination === 'coach-my-journal-2' ? '/coach-journal-2?workspace=mine' : action.destination === 'coach-journal-2' ? '/coach-journal-2?workspace=journals' : action.destination === 'journal-2' ? '/journal-2' : action.destination === 'review' ? '/program-review' : action.destination === 'scouting' ? '/scouting' : action.destination === 'participants' ? '/athletes' : action.destination === 'settings' ? '/program-settings' : action.destination === 'episodes' ? '/episodes' : action.destination === 'coach-my-journal' ? '/coach-journal?workspace=mine' : action.destination === 'coach-journal' ? '/coach-journal' : action.destination === 'journal' ? '/journal' : ''}` : undefined,
      disabled: !action.destination, subtitle: action.destination ? undefined : t('developmentProgram.unavailable'),
    }))
    return []
  })

  const feedback = useAthleteFeedback(computed(() => eventMode.value === 'staff' && allowFeedback.value))
  const pendingFeedbackCount = computed(() => feedback.query.isSuccess.value && feedback.query.data.value?.view === 'staff'
    ? feedback.query.data.value.outstandingCount ?? feedback.query.data.value.records.filter(record => record.request && !record.feedback?.published).length : 0)
  // Reuse Event Home's sections and grouping instead of maintaining a second staff menu.
  const navigation = computed(() => eventHomeNavigation({
    betaEnabled:beta.value,
    t, capitalize, pluralizeModelNoun, currentView:'admin', developmentProgram:developmentProgram.value,
    allowSettings:allowSettings.value, allowFeedback:allowFeedback.value,
    allowParticipantList:allowParticipantList.value, allowParticipantImport:hasAnyRole(['admin','owner']),
    allowCapture:hasAnyRole(['admin','owner','headcoach','assistantcoach','selector']),
    allowScanScoresheets:allowScanScoresheets.value, allowSelectTeams:allowSelectTeams.value,
    allowAssessmentAnalysis:hasAnyRole(['owner','admin','headcoach','programcoach','sysadmin']),
    allowAssignments:hasAnyRole(['headcoach','admin','owner','sysadmin']),
    allowCommunications:hasAnyRole(['admin','owner','sysadmin']),
    canUseDevelopment:hasAnyRole(['admin','owner','headcoach','assistantcoach','programcoach']),
    allowJournal:homeData.value?.features?.athleteDevelopment?.available === true && hasAnyRole(['admin','owner','headcoach','assistantcoach','programcoach']),
    participantNoun:contextData.value?.event.participantNoun || participantNoun.value,
    participantLabel:capitalize(contextData.value?.event.participantNoun || participantNoun.value),
    participantPluralLabel:pluralize(contextData.value?.event.participantNoun || participantNoun.value),
    sessionHeading:capitalize(pluralize(contextData.value?.event.sessionNoun || sessionNoun.value)),
    tryoutContext:contextData.value || null, pendingFeedbackCount:pendingFeedbackCount.value, feedbackBadgeLabel:t('athleteFeedback.pendingCount',{count:pendingFeedbackCount.value}),
  }))
  const activeSection = computed(() => route.path === eventHomePath.value ? String(route.query.section || 'overview') : route.path.endsWith('/prototype-decision-room') ? 'prototype-decision-room' : route.path.endsWith('/decision-room') ? 'decision-room' : '')
  function selectSection(section:string) {
    if(section === 'decision-room' || section === 'prototype-decision-room') {void router.push(`${eventHomePath.value}/${section}`);return}
    void router.push({path:eventHomePath.value,query:{section:section === 'overview' ? undefined : section,...(['journals','broadcasts','surveys'].includes(section)?{workspace:section === 'journals'?'recent':section}:{})}})
  }

  const runAction = (action: EventAction) => {
    if (action.disabled || !action.to) return
    router.push(action.to)
  }

  function hasAnyRole(roleNames: string[]) {
    return roleNames.some((role) => roles.value[role as keyof typeof roles.value] === true)
  }

  function capitalize(value: string) {
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : value
  }

  function pluralize(value: string) {
    if (!value) return value
    if (value.endsWith('s')) return value
    if (value.endsWith('y')) return `${value.slice(0, -1)}ies`
    return `${value}s`
  }

</script>



<style scoped>
.event-navigation-sidebar{display:grid;gap:5px;padding:4px;background:white;border:1px solid var(--cs-border);border-radius:10px}
.event-navigation-sidebar button{display:flex;align-items:center;gap:12px;padding:12px;text-align:left;color:var(--cs-text);border-radius:8px;min-height:48px}.event-navigation-sidebar button:hover{background:var(--cs-selection)}.event-navigation-sidebar button:focus-visible{outline:2px solid var(--cs-action)}.event-navigation-sidebar button:disabled{opacity:.5}.event-navigation-sidebar button span{display:grid;gap:4px}.event-navigation-sidebar small{color:var(--cs-muted);font-size:.8rem}
</style>
